import { makeAutoObservable, runInAction, computed, action, observable } from 'mobx';
import moment, { Moment } from 'moment';
import 'moment-timezone';
import API, { getStudentProgress, markStreakRewardViewed, StudentProgress, getLeaderboard, Leaderboard } from '../api/API';
import { FULL_PAGE } from '../components/SpinnerWrapper';
import { EventTicket, SellingItem, SellingItemCategory, User, DebtInfo } from '../config/Registration';
import { StepName } from '../config/Wizard';
import RootStore from './RootStore';
import SpinnerStore from './SpinnerStore';
import WizardStore from './WizardStore';
import { AlertProps } from 'react-bootstrap/Alert';
import EventPhotoStore from './EventPhotoStore';
import { AuthStore } from './AuthStore';
import { getPhoneNumberFromToken, isTokenExpired } from '../util';
import { EVENTS_AD_COOLDOWN_KEY, wasShownWithin } from '../util/displayCooldown';
import { clearPendingLoginOtp, getPendingLoginOtp, LoginOtpMethod, savePendingLoginOtp } from './LoginOtpSession';

const EVENTS_AD_COOLDOWN_MINUTES = 60;

export const RETURN_TO_RENTAL_TAB_KEY = 'returnToRentalTab';

export interface Error {
  code: ErrorCode;
  level?: AlertProps['variant'];
  debtInfo?: DebtInfo;
  customMessage?: string;
}

export enum ErrorCode {
  USER_DOES_NOT_EXIST = 'USER_DOES_NOT_EXIST',
  NOT_ENOUGH_COINS = 'NOT_ENOUGH_COINS',
  NO_TRAINING_TIMES = 'NO_TRAINING_TIMES',
  DEBT_FOUND = 'DEBT_FOUND',
  REGISTRATION_EXISTS_FOR_TRAINING = 'REGISTRATION_EXISTS_FOR_TRAINING',
  LEVEL_REQUIREMENT_NOT_MET = 'LEVEL_REQUIREMENT_NOT_MET',
  DUPLICATE_PHONE = 'DUPLICATE_PHONE',
  NO_PACKAGE = 'NO_PACKAGE',
  THROTTLING_PHONE = 'THROTTLING_PHONE',
  THROTTLING_EMAIL = 'THROTTLING_EMAIL',
  PENDING_EARLY_BIRD = 'PENDING_EARLY_BIRD',
  BACKEND_MESSAGE = 'BACKEND_MESSAGE',
}

export const getBlockingAccountError = (user: User): Error | null => {
  if (user.debtInfo) {
    return { code: ErrorCode.DEBT_FOUND, debtInfo: user.debtInfo };
  }

  return null;
};

export const getActiveCategoryAfterRefresh = (
  activeCategory: number,
  previousCategoryCount: number,
  nextCategoryCount: number,
  hasRentalTab: boolean,
  hasPrivateClassesTab: boolean
): number => {
  if (previousCategoryCount === 0) {
    return activeCategory >= 0 && activeCategory < nextCategoryCount ? activeCategory : 0;
  }

  const previousRentalTabIndex = previousCategoryCount;
  if (hasRentalTab && activeCategory === previousRentalTabIndex) {
    return nextCategoryCount;
  }

  const previousPrivateClassesTabIndex = previousCategoryCount + (hasRentalTab ? 1 : 0);
  if (hasPrivateClassesTab && activeCategory === previousPrivateClassesTabIndex) {
    return nextCategoryCount + (hasRentalTab ? 1 : 0);
  }

  if (activeCategory >= 0 && activeCategory < previousCategoryCount && activeCategory < nextCategoryCount) {
    return activeCategory;
  }

  return 0;
};

export class RegistrationStore {
  @observable public user: User;
  @observable public sellingItemCategories: SellingItemCategory[] = [];
  @observable public selectedItems: SellingItem[] = [];
  @observable public selectedTickets: EventTicket[] = [];
  @observable public selectedRentals: any[] = [];
  @observable public error: Error;
  @observable public partialSuccesses = [];
  @observable public partialFailures = [];
  @observable public activeCategory: number = 0;
  @observable public selectedEventId: number | null = null;
  @observable public purchasedEventId: number | null = null;
  @observable public loginPhoneNumber: string | null = null;
  @observable public loginEmail: string | null = null;
  @observable public loginMethod: LoginOtpMethod = 'phone';
  @observable public displayedPhoneNumber: string | null = null;
  @observable public displayedLoginTarget: string | null = null;
  @observable public selectedDate: Date | null = null;
  @observable public lastDataFetchTime: number = 0;
  @observable public studentProgress: StudentProgress | null = null;
  @observable public leaderboard: Leaderboard | null = null;
  private leaderboardRequest = 0;
  private readonly spinnerStore: SpinnerStore;
  private readonly wizardStore: WizardStore;
  private readonly authStore: AuthStore;
  private readonly eventPhotoStore: EventPhotoStore;
  private readonly STALE_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutes
  private refreshInterval: NodeJS.Timeout | null = null;
  @observable private _discountRange: { min: number; max: number } = { min: 5, max: 25 };
  private shouldRestoreRentalTab = false;

  constructor(readonly rootStore: RootStore) {
    makeAutoObservable(this);
    this.spinnerStore = rootStore.spinnerStore;
    this.wizardStore = rootStore.wizardStore;
    this.authStore = rootStore.authStore;
    this.eventPhotoStore = rootStore.eventPhotoStore;

    if (typeof window !== 'undefined') {
      const shouldReturnToRental = sessionStorage.getItem(RETURN_TO_RENTAL_TAB_KEY);
      if (shouldReturnToRental === 'true') {
        this.shouldRestoreRentalTab = true;
        sessionStorage.removeItem(RETURN_TO_RENTAL_TAB_KEY);
      }
    }

    this.restorePendingLoginOtp();
    
    this.startPeriodicRefresh();
  }

  private startPeriodicRefresh = () => {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    
    this.refreshInterval = setInterval(() => {
      if (this.isDataStale) {
        this.refreshData();
      }
    }, 60 * 1000); 
  }

  private stopPeriodicRefresh = () => {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  @computed
  get isDataStale(): boolean {
    // Don't consider data stale if tab is inactive
    if (document.hidden) {
      return false;
    }
    if (this.isAuthenticationStep) {
      return false;
    }
    return Date.now() - this.lastDataFetchTime > this.STALE_THRESHOLD_MS;
  }

  @computed
  get isAuthenticationStep(): boolean {
    return this.wizardStore.currentStepName === StepName.REGISTRATION_AUTHENTICATE ||
      this.wizardStore.currentStepName === StepName.REGISTRATION_AUTHENTICATE_OTP;
  }

  @action
  private refreshData = async () => {
    try {
      await this.loadSellingItems(this.selectedDate || undefined, true); 
    } catch (error) {
    }
  }

  @action
  public setLoginPhoneNumber = (phoneNumber: string) => {
    this.loginMethod = 'phone';
    this.loginPhoneNumber = phoneNumber;
    this.loginEmail = null;
  }

  @action
  public setLoginEmail = (email: string) => {
    this.loginMethod = 'email';
    this.loginEmail = email;
    this.loginPhoneNumber = null;
  }

  @action
  public setDisplayedPhoneNumber = (phoneNumber: string) => {
    this.displayedPhoneNumber = phoneNumber;
    this.displayedLoginTarget = phoneNumber;
  }

  @action
  public savePendingLoginOtp = (
    loginTarget: string,
    displayedLoginTarget: string,
    loginMethod: LoginOtpMethod = 'phone'
  ) => {
    this.loginMethod = loginMethod;
    this.loginPhoneNumber = loginMethod === 'phone' ? loginTarget : null;
    this.loginEmail = loginMethod === 'email' ? loginTarget : null;
    this.displayedPhoneNumber = loginMethod === 'phone' ? displayedLoginTarget : null;
    this.displayedLoginTarget = displayedLoginTarget;
    savePendingLoginOtp(loginTarget, displayedLoginTarget, loginMethod);
  }

  @action
  public clearPendingLoginOtp = () => {
    this.loginPhoneNumber = null;
    this.loginEmail = null;
    this.loginMethod = 'phone';
    this.displayedPhoneNumber = null;
    this.displayedLoginTarget = null;
    clearPendingLoginOtp();
  }

  @action
  public updateUser = (user: User) => {
    this.user = {
      ...this.user,
      ...user,
      latestTrainingPackage: user.latestTrainingPackage || this.user?.latestTrainingPackage,
      debtInfo: user.debtInfo || this.user?.debtInfo,
      bookingHistory: user.bookingHistory || this.user?.bookingHistory,
      referralToken: user.referralToken || this.user?.referralToken,
    };
  }

  @action
  public clearError = (): void => {
    this.error = null;
  }

  @action
  public resetActiveCategory = (): void => {
    this.activeCategory = 0;
    this.selectedEventId = null;
  };

  @action
  public restart = (): void => {
    this.stopPeriodicRefresh();

    if (this.authStore.isUserFlow) {
      this.wizardStore.setStep(StepName.REGISTRATION_SELECT_TRAINING)
      this.error = null;
      this.selectedItems = [];
      this.selectedTickets = [];
      this.selectedRentals = [];
      this.purchasedEventId = null;
      this.resetActiveCategory();
      // Reload data with the currently selected date
      const currentDate = this.rootStore.studioConfigStore.selectedDate;
      this.loadSellingItems(currentDate, true);
    } else {
      this.clearPendingLoginOtp();
      this.wizardStore.setStep(StepName.REGISTRATION_AUTHENTICATE);
      this.error = null;
      this.user = null;
      this.selectedItems = [];
      this.selectedTickets = [];
      this.selectedRentals = [];
      this.sellingItemCategories = [];
      this.selectedDate = null;
      this.purchasedEventId = null;
      this.studentProgress = null;
      this.leaderboard = null;
      this.resetActiveCategory();
    }

    this.startPeriodicRefresh();
  };

  @action
  private restorePendingLoginOtp = () => {
    const token = localStorage.getItem('jwtToken');
    if (this.authStore.isAuthenticated && token && !isTokenExpired(token)) {
      clearPendingLoginOtp();
      return;
    }

    const pendingLoginOtp = getPendingLoginOtp();
    if (!pendingLoginOtp) {
      return;
    }

    this.loginMethod = pendingLoginOtp.method || 'phone';
    this.loginPhoneNumber = this.loginMethod === 'phone' ? pendingLoginOtp.phoneNumber : null;
    this.loginEmail = this.loginMethod === 'email' ? pendingLoginOtp.email : null;
    this.displayedPhoneNumber = this.loginMethod === 'phone' ? pendingLoginOtp.displayedPhoneNumber : null;
    this.displayedLoginTarget = pendingLoginOtp.displayedLoginTarget || pendingLoginOtp.displayedPhoneNumber;
    this.wizardStore.setStep(StepName.REGISTRATION_AUTHENTICATE_OTP);
  }

  @action
  public restartAfterEventTicketPurchase = async (): Promise<void> => {
    this.stopPeriodicRefresh();

    // Reset the selected date to today
    this.rootStore.studioConfigStore.resetSelectedDate();
    this.selectedDate = null;

    // Check auth state before proceeding
    this.authStore.checkAuth();
    
    // Check if token is expired
    const token = localStorage.getItem('jwtToken');
    const tokenExpired = token ? isTokenExpired(token) : true;
    
    if (this.authStore.isUserFlow && this.authStore.isAuthenticated && !tokenExpired) {
      try {
        await this.loadSellingItems(undefined, true);
        
        // Store the event ID before clearing it
        const targetEventId = this.purchasedEventId;
        
        // Find and set the Events category as active
        const eventsCategory = this.sellingItemCategories.findIndex(cat => cat.name === "Events");
        if (eventsCategory >= 0) {
          this.activeCategory = eventsCategory;
        }
        
        // Set the purchased event as selected
        if (targetEventId) {
          this.selectedEventId = targetEventId;
        }
        
        this.error = null;
        this.selectedItems = [];
        this.selectedTickets = [];
        this.selectedRentals = [];
        
        // Navigate to the event selection without triggering token check
        this.wizardStore.currentStepName = StepName.REGISTRATION_SELECT_TRAINING;
        
        // Clear purchased event ID after using it
        this.purchasedEventId = null;
        
        // Scroll to tickets section after DOM update
        setTimeout(() => {
          if (targetEventId) {
            this.scrollToTicketsSectionWithRetry();
          }
          // Start periodic refresh only after everything is settled
          setTimeout(() => {
            this.startPeriodicRefresh();
          }, 1000);
        }, 500);
      } catch (error) {
        console.error('Error in restartAfterEventTicketPurchase:', error);
        // Fallback to normal restart if there's an error
        this.restart();
      }
    } else {
      // Fallback to normal restart for non-user flow or expired auth
      this.restart();
    }
  };

  @action
  public restartAfterRentalBooking = async (): Promise<void> => {
    this.stopPeriodicRefresh();

    // Check auth state before proceeding
    this.authStore.checkAuth();
    
    // Check if token is expired
    const token = localStorage.getItem('jwtToken');
    const tokenExpired = token ? isTokenExpired(token) : true;
    
    if (this.authStore.isUserFlow && this.authStore.isAuthenticated && !tokenExpired) {
      try {
        await this.loadSellingItems(undefined, true);
        
        // Set the Rental category as active (it's at index sellingItemCategories.length)
        this.activeCategory = this.sellingItemCategories.length;
        
        this.error = null;
        this.selectedItems = [];
        this.selectedTickets = [];
        this.selectedRentals = [];
        this.purchasedEventId = null;
        this.selectedEventId = null;
        
        // Navigate to the selection screen
        this.wizardStore.currentStepName = StepName.REGISTRATION_SELECT_TRAINING;
        
        // Start periodic refresh after everything is settled
        setTimeout(() => {
          this.startPeriodicRefresh();
        }, 1000);
      } catch (error) {
        console.error('Error in restartAfterRentalBooking:', error);
        // Fallback to normal restart if there's an error
        this.restart();
      }
    } else {
      // Fallback to normal restart for non-user flow or expired auth
      this.restart();
    }
  };

  @action
  private scrollToTicketsSectionWithRetry = (attempt: number = 1, maxAttempts: number = 5) => {
    const bookedTicketsSection = document.getElementById('booked-tickets-section');
    if (bookedTicketsSection) {
      bookedTicketsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // Try alternative selectors as fallback
    const detailCards = document.querySelectorAll('.detail-card');
    const bookedTicketCard = Array.from(detailCards).find(card =>
      card.textContent?.toLowerCase().includes('your booked tickets')
    );

    if (bookedTicketCard) {
      bookedTicketCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // Element not found, retry if we haven't exceeded max attempts
    if (attempt < maxAttempts) {
      setTimeout(() => {
        this.scrollToTicketsSectionWithRetry(attempt + 1, maxAttempts);
      }, 300);
    } else {
      // Final fallback: scroll to last detail card
      if (detailCards.length > 0) {
        const lastCard = detailCards[detailCards.length - 1] as HTMLElement;
        lastCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  @action
  public clearSelectedItems = (): void => {
    this.selectedItems = [];
    this.selectedTickets = [];
    this.selectedRentals = [];
    this.sellingItemCategories = [];
    this.selectedDate = null;
    this.purchasedEventId = null;
    this.resetActiveCategory();
  }

  @action
  public loadUser = async (phoneNumber: string) => {
    this.spinnerStore.show(FULL_PAGE);

    try {
      const result = await API.get(`/user/phone/${encodeURIComponent(phoneNumber)}`, {
        dateTime: moment.utc().toISOString() // Use consistent UTC timestamp
      });
      
      this.handleUserResponse(result);
      if (!this.error && this.user?.id) {
        await this.rootStore.eventPhotoStore.loadEventPhotos(this.user.id);

        const hasPhotos = this.rootStore.eventPhotoStore.promotionItems.length > 0;
        const eventsAdOnCooldown = wasShownWithin(EVENTS_AD_COOLDOWN_KEY, this.user.id, EVENTS_AD_COOLDOWN_MINUTES);
        if (hasPhotos && !eventsAdOnCooldown) {
          this.wizardStore.setStep(StepName.EVENTS_VIEW);
        } else {
          this.wizardStore.setStep(StepName.REGISTRATION_SELECT_TRAINING);
        }
      }
    } catch (error: any) {
      // Handle specific error cases
      if (error?.message === 'DUPLICATE_PHONE') {
        this.error = { code: ErrorCode.DUPLICATE_PHONE };
        this.wizardStore.setStep(StepName.REGISTRATION_FAILED);
      } else if (
        error?.status === 403 ||
        error === 'Access forbidden - user may no longer exist'
      ) {
        // Handle case where user no longer exists - set appropriate error
        this.error = { code: ErrorCode.USER_DOES_NOT_EXIST };
        this.wizardStore.setStep(StepName.REGISTRATION_FAILED);
      } else {
        // For other errors, still throw to maintain existing behavior
        throw error;
      }
    } finally {
      this.spinnerStore.hide(FULL_PAGE);
    }
  };

  // Populate just the `user` object from the JWT after a hard page load (e.g. the Stripe
  // checkout redirect back to /packages-membership). Unlike loadUser this has no spinner,
  // wizard-step, or events-ad side effects and never routes to REGISTRATION_FAILED, so it
  // is safe to call from standalone pages that only need the current user.
  @action
  public ensureUserLoaded = async (): Promise<void> => {
    if (this.user?.id) {
      return;
    }
    let phoneNumber: string | null;
    try {
      phoneNumber = getPhoneNumberFromToken();
    } catch (e) {
      return;
    }
    if (!phoneNumber) {
      return;
    }
    const result = await API.get(`/user/phone/${encodeURIComponent(phoneNumber)}`, {
      dateTime: moment.utc().toISOString()
    });
    if (result) {
      runInAction(() => {
        this.user = JSON.parse(result);
      });
    }
  };

  @action
  public refreshUser = async (): Promise<void> => {
    let phoneNumber: string | null;
    try {
      phoneNumber = getPhoneNumberFromToken();
    } catch (e) {
      return;
    }
    if (!phoneNumber) {
      return;
    }

    const result = await API.get(`/user/phone/${encodeURIComponent(phoneNumber)}`, {
      dateTime: moment.utc().toISOString()
    });
    if (result) {
      this.updateUser(JSON.parse(result));
    }
  };

  @action
  private handleUserResponse = (result: any): void => {
    if (result) {
      this.user = JSON.parse(result);
      if (this.user?.id) {
        this.loadStudentProgressSilent();
        this.loadLeaderboard();
      }
    } else {
      this.error = { code: ErrorCode.USER_DOES_NOT_EXIST };
    }

    if (!this.error) {
      const blockingAccountError = getBlockingAccountError(this.user);
      if (blockingAccountError) {
        this.error = blockingAccountError;
      }
    }

    if (this.error) {
      this.wizardStore.setStep(StepName.REGISTRATION_FAILED);
    }
  };

  @action
  public activateEarlyBirdPackage = async (packageId: number) => {
    this.spinnerStore.show(FULL_PAGE);
    try {
      const result = await API.post(`/packages/${packageId}/activate`);
      runInAction(() => {
        this.user = JSON.parse(result);
        this.error = null;
        this.wizardStore.setStep(StepName.REGISTRATION_SELECT_TRAINING);
      });
    } finally {
      this.spinnerStore.hide(FULL_PAGE);
    }
  };

  @action
  public unselectAllSellingItems = () => {
    this.sellingItemCategories.forEach(category => category.items.forEach(item => item.selected = false))
  }

  @action
  public unselectAllEventTickets = () => {
    this.sellingItemCategories.forEach(category => category.tickets.forEach(ticket => ticket.selected = false))
  }

  @action
  public selectSellingItems = () => {
    if (!this.user.latestTrainingPackage) {
      this.error = {
        code: this.user.pendingEarlyBirdPackage ? ErrorCode.PENDING_EARLY_BIRD : ErrorCode.NO_PACKAGE,
        level: this.user.pendingEarlyBirdPackage ? 'info' : undefined
      };
      this.wizardStore.setStep(StepName.REGISTRATION_FAILED);
      return;
    }

    const itemsCost = this.preselectedSellingItems.reduce((accumulator, value) => {
      return accumulator + value.cost;
    }, 0);

    const itemsDiscount = this.preselectedSellingItems.reduce((accumulator, value) => {
      return accumulator + (value.discountValue ?? 0);
    }, 0);

    const ticketsCost = this.preselectedTickets.reduce((accumulator, value) => {
      return accumulator + value.effectiveCoinsPrice;
    }, 0);

    if (this.user.latestTrainingPackage.availableCoins < (itemsCost - itemsDiscount) + ticketsCost) {
      this.error = { code: ErrorCode.NOT_ENOUGH_COINS }
    }

   this.selectedItems = this.preselectedSellingItems;
   this.selectedTickets = this.preselectedTickets;

    if (this.error) {
      this.wizardStore.setStep(StepName.REGISTRATION_FAILED);
    } else {
      this.addRegistrations();
    }
  }

  @action
  public addRegistrations = async () => {
    this.spinnerStore.show(FULL_PAGE);
    try {
      const successes = [];
      const failures = [];

      // Process selected items
      for (const selectedItem of this.selectedItems) {
        try {
          const result = await this.addRegistration(selectedItem);
          const parsedResult = JSON.parse(result);
          const cost = parsedResult.cost;
          const discountAmount = parsedResult.discountAmount ?? selectedItem.discountValue;

          runInAction(() => {
            this.user.latestTrainingPackage.availableCoins -= (cost - discountAmount);
          });
          successes.push(selectedItem);
        } catch (error) {
          failures.push({ item: selectedItem, error });
        }
      }

      // Process selected tickets
      for (const eventTicket of this.selectedTickets) {
        try {
          const result = await this.createEventRegistration(eventTicket);
          const cost = JSON.parse(result); // Event registration returns cost directly

          // Update available coins based on actual cost from backend
          runInAction(() => {
            const pkg = this.user.latestTrainingPackage;
            pkg.availableCoins -= cost;
            if (cost > 0 && pkg.availableCoins <= 0) {
              pkg.isActive = false;
            }
            // Track the event ID for successful ticket purchases
            this.purchasedEventId = eventTicket.eventId;
          });
          successes.push(eventTicket);
        } catch (error) {
          failures.push({ item: eventTicket, error });
        }
      }

      this.resetActiveCategory();

      if (successes.length > 0 && this.user?.id) {
        try {
          await this.refreshUser();
        } catch (error) {
          console.error('Failed to refresh user booking history:', error);
        }
        await this.loadStudentProgressSilent();
        this.loadLeaderboard();
      }

      // Determine the next step based on successes and failures
      if (successes.length > 0 && failures.length === 0) {
        this.wizardStore.setStep(StepName.REGISTRATION_SUCCESS);
      } else if (successes.length === 0 && failures.length > 0) {
        const errorMessage = failures[0].error.message;
        
        // Check if the error message is a known ErrorCode
        if (Object.values(ErrorCode).includes(errorMessage as ErrorCode)) {
          this.error = { code: errorMessage as ErrorCode };
        } else {
          // Use a generic error code but store the custom message
          this.error = { 
            code: ErrorCode.BACKEND_MESSAGE, // Use as fallback, but customMessage will override
            customMessage: errorMessage 
          };
        }

        this.wizardStore.setStep(StepName.REGISTRATION_FAILED);
      } else {
        this.partialSuccesses = successes;
        this.partialFailures = failures;
        this.wizardStore.setStep(StepName.REGISTRATION_PARTIAL_SUCCESS);
      }
    } finally {
      this.spinnerStore.hide(FULL_PAGE);
    }
  };

    

  private addRegistration = async (item: SellingItem) => {
    const registrationTime = this.constructRegistrationTime(item);
    
    return await API.post(`/register`, {
      trainingPeriodId: item.trainingPeriodId,
      sellingItemId: item.sellingItemId,
      discountRuleId: item.discountRuleId,
      time: registrationTime,
      userId: this.user.id, // Always send userId, backend decides which flow to use
    });
  }

  private constructRegistrationTime = (item: SellingItem): moment.Moment => {
    if (item.time) {
      // Use the selected date with the hour and minute from the item's time
      // IMPORTANT: Interpret the date/time in Europe/Tallinn timezone first, then convert to UTC
      // This ensures that regardless of the user's local timezone, we're booking for the correct
      // time slot in the studio's timezone (Europe/Tallinn)
      return moment.tz(item.time.date, 'Europe/Tallinn')
        .hours(item.time.hour)
        .minutes(item.time.minute)
        .seconds(0)
        .milliseconds(0)
        .utc();
    } else {
      // Fallback to current time if no time or selectedDate
      return this.now.utc();
    }
  }

  private createEventRegistration = async (item: EventTicket) => {
    // For event registrations, we only need to register for the specific event
    // The backend will handle the training period registration internally
    const result = await API.post(`/register/event`, {
      eventId: item.eventId,
      ticketTypeId: item.ticketTypeId,
      userId: this.user.id, // Always send userId, backend decides which flow to use
    });
  
    return result;
  }

  @action
  public loadDiscountRange = async () => {
    const { studioConfigs } = this.rootStore.studioConfigStore;
    if (!studioConfigs) return;

    const endDateTime = moment(studioConfigs.serverDateTime)
      .add(studioConfigs.checkInFutureDays, 'days')
      .utc()
      .toISOString();

    try {
      const range = await API.getDiscountRange(endDateTime);
      runInAction(() => {
        this._discountRange = {
          min: range.minDiscount,
          max: range.maxDiscount
        };
      });
    } catch (error) {
      console.error('Failed to load discount range:', error);
      // Keep default values in case of error
      runInAction(() => {
        this._discountRange = { min: 5, max: 25 };
      });
    }
  };

  @action
  public loadSellingItems = async (selectedDate?: Date, forceRefresh = false): Promise<boolean> => {
    if (!forceRefresh && !this.isDataStale && this.sellingItemCategories.length > 0) {
      return true;
    }

    this.spinnerStore.show(FULL_PAGE);
    
    // Load discount range when loading selling items
    await this.loadDiscountRange();
    
    // Use the provided date or current date
    const dateToUse = selectedDate || this.now.toDate();
    runInAction(() => {
      this.selectedDate = dateToUse;
    });
    // Always generate UTC timestamp for API consistency across environments
    const dateTime = selectedDate ? 
      moment(selectedDate).utc().toISOString() : 
      moment.tz('Europe/Tallinn').utc().toISOString();
    
    try {
      const result = await API.get('/selling-items', {
        dateTime: dateTime,
        userId: this.user?.id
      });

      const parsedResult = JSON.parse(result);
      runInAction(() => {
        const previousCategoryCount = this.sellingItemCategories.length;
        this.setSellingCategories(parsedResult.map(category => new SellingItemCategory(category)));
        this.lastDataFetchTime = Date.now();

        const studioConfigStore = this.rootStore.studioConfigStore;
        const hasRentalTab = studioConfigStore.rentEnabled;
        const hasPrivateClassesTab = studioConfigStore.isPrivateClassesAllowed(this.user?.id);
        const rentalTabIndex = this.sellingItemCategories.length;

        this.activeCategory = getActiveCategoryAfterRefresh(
          this.activeCategory,
          previousCategoryCount,
          this.sellingItemCategories.length,
          hasRentalTab,
          hasPrivateClassesTab
        );

        if (this.shouldRestoreRentalTab && hasRentalTab) {
          this.activeCategory = rentalTabIndex;
          this.shouldRestoreRentalTab = false;
        }

        if (this.error) {
          this.wizardStore.setStep(StepName.REGISTRATION_FAILED);
        }
      });
      return true;
    } catch (error) {
      console.error('Failed to load selling items:', error);
      this.error = { code: ErrorCode.NO_TRAINING_TIMES, level: 'error' };
      return false;
    } finally {
      this.spinnerStore.hide(FULL_PAGE);
    }
  }

  @action
  toBookingHistory = () => {
    this.wizardStore.setStep(StepName.REGISTRATION_HISTORY);
  }

  @action
  public loadStudentProgressSilent = async () => {
    if (!this.user?.id) return;
    try {
      const result = await getStudentProgress(this.user.id);
      runInAction(() => {
        this.studentProgress = result;
      });
    } catch (error) {
      // Silently fail — streak in header is non-critical
    }
  }

  @action
  public loadLeaderboard = async (styleId?: number) => {
    if (!this.user?.id) return;
    const userId = this.user.id;
    const request = ++this.leaderboardRequest;
    try {
      const result = await getLeaderboard(userId, styleId);
      runInAction(() => {
        if (request === this.leaderboardRequest && userId === this.user?.id) this.leaderboard = result;
      });
    } catch (error) {
      // Silently fail — leaderboard in header is non-critical
      // Keep the previous board available if switching fails.
      runInAction(() => {
        if (styleId == null && request === this.leaderboardRequest && userId === this.user?.id) this.leaderboard = null;
      });
    }
  }

  @action
  public toLeaderboard = () => {
    this.clearSelectedItems();
    this.leaderboard = null;
    this.loadLeaderboard();
    this.wizardStore.setStep(StepName.LEADERBOARD);
  }

  public markStreakRewardViewed = async (rewardId: number) => {
    if (!this.user?.id) return;

    try {
      await markStreakRewardViewed(rewardId, this.user.id);
      runInAction(() => {
        const reward = this.studentProgress?.attendanceStats?.streakRewards?.find(item => item.id === rewardId);
        if (reward) {
          reward.viewedAt = new Date().toISOString();
          if (reward.status === 'available') {
            reward.status = 'viewed';
          }
        }
      });
    } catch (error) {
      console.error('Failed to mark streak reward viewed:', error);
    }
  }

  public loadStudentProgress = async () => {
    if (!this.user?.id) {
      throw new Error('User not loaded');
    }

    this.spinnerStore.show(FULL_PAGE);

    try {
      const result = await getStudentProgress(this.user.id);
      runInAction(() => {
        this.studentProgress = result;
      });
    } catch (error) {
      console.error('Failed to load student progress:', error);
      runInAction(() => {
        this.error = { code: ErrorCode.BACKEND_MESSAGE, customMessage: 'Failed to load progress data' };
      });
    } finally {
      this.spinnerStore.hide(FULL_PAGE);
    }
  }

  @action
  public toMyProgress = () => {
    this.clearSelectedItems();
    this.loadStudentProgress();
    this.wizardStore.setStep(StepName.MY_PROGRESS);
  }

  @action
  public setActiveCategory = (category: number) => {
    this.activeCategory = category;
  }

  @action
  public toSellingItems = (category?: number) => {
    if (category !== undefined) {
      this.setActiveCategory(category);
    }
    this.wizardStore.setStep(StepName.REGISTRATION_SELECT_TRAINING);
  }

  @action
  setSellingCategories = (sellingItemCategories: SellingItemCategory[]) => {
    this.sellingItemCategories = sellingItemCategories;
  }

  @computed
  get isHistoryStep(): boolean {
    return this.wizardStore.currentStepName === StepName.REGISTRATION_HISTORY;
  }

  @computed
  get isProgressStep(): boolean {
    return this.wizardStore.currentStepName === StepName.MY_PROGRESS;
  }

  @computed
  get isLeaderboardStep(): boolean {
    return this.wizardStore.currentStepName === StepName.LEADERBOARD;
  }

  @computed
  get isCheckinStep(): boolean {
    return this.wizardStore.currentStepName === StepName.REGISTRATION_SELECT_TRAINING;
  }

  @computed
  get discountRange() {
    return this._discountRange;
  }

  @computed
  get preselectedSellingItems(): SellingItem[] {
    return this.sellingItemCategories.flatMap(category => category.items.filter(item => item.selected));
  }

  @computed
  get preselectedTickets(): EventTicket[] {
    return this.sellingItemCategories.flatMap(category => category.tickets.filter(ticket => ticket.selected));
  }


  @computed
  get hasPreselectedItems(): boolean {
    return this.preselectedSellingItems.length > 0 || this.preselectedTickets.length > 0;
  }

  @computed
  get hasOnlyTicketsPurchased(): boolean {
    return this.selectedItems.length === 0 && this.selectedTickets.length > 0;
  }

  @computed
  get hasOnlyRentalsBooked(): boolean {
    return this.selectedItems.length === 0 && this.selectedTickets.length === 0 && this.selectedRentals.length > 0;
  }
  
  public formatEventDate = (startTimeString: string, endTimeString: string): string => {
    if (!startTimeString) return "";
  
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    const formatSingleDate = (date: Date) => new Intl.DateTimeFormat('en-US', options).format(date);
  
    try {
      const startDate = new Date(startTimeString);
      
      // Default format for single day events or if endTime is missing
      let formattedDate = formatSingleDate(startDate);
  
      if (endTimeString) {
        const endDate = new Date(endTimeString);
        // Adjust end date by subtracting 8 hours
        const adjustedEndDate = new Date(endDate.getTime() - 8 * 60 * 60 * 1000);
  
        // Check if the adjusted end date is on a later day than the start date
        const startDateDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const adjustedEndDateDay = new Date(adjustedEndDate.getFullYear(), adjustedEndDate.getMonth(), adjustedEndDate.getDate());
  
        if (adjustedEndDateDay > startDateDay) {
          formattedDate = `${formatSingleDate(startDate)} - ${formatSingleDate(endDate)}`;
        
        }
      }
      
      return formattedDate;
    } catch (e) {
      console.error("Error formatting date:", e);
      return ""; 
    }
  };

  public categoryActiveItems = (category: SellingItemCategory): SellingItem[] | EventTicket[] => {
    if (!this.user) return [];

    if (category.name === 'Events') {
      return category.tickets;
    }

    const serverDate = this.selectedDate || this.now.toDate();
    const currentDate = this.now.toDate();
    
    // Check if we're looking at future dates
    const isCurrentDate = moment(serverDate).isSame(currentDate, 'day');
    
    return category.items.filter(item => {
      if (!item.time) return true;
      
      // Only apply time filtering for current date
      if (isCurrentDate) {
        return this.itemMoment(item, serverDate).diff(moment(serverDate)) >= -(item.time.duration + 30) * 60 * 1000;
      }
      
      // For future dates, show all classes
      return true;
    });
  };

  private itemMoment = (item: SellingItem, baseDate: Date) => {
    return moment(baseDate).hours(item.time.hour).minutes(item.time.minute).startOf('minute');
  }


  // For easier mocking
  public get now(): Moment {
    // return moment('2022-08-25 17:30');
    return moment();
  }
}

export default RegistrationStore;
